import moment from 'moment';

export const DEFAULT_EXPIRING_INTERVAL_DAYS = 30;
export const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD';

const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';

// Internal keys, never shown to users: screen text only comes from VALIDITY_LABEL.
export const VALIDITY_STATUS = Object.freeze({
	ACTIVE: 'active',
	EXPIRING: 'expiring',
	EXPIRED: 'expired',
});

export const VALIDITY_LABEL = Object.freeze({
	[VALIDITY_STATUS.ACTIVE]: 'Active',
	[VALIDITY_STATUS.EXPIRING]: 'Expiring',
	[VALIDITY_STATUS.EXPIRED]: 'Expired',
});

export const parseExpiringInterval = (setting) => {
	const interval = parseInt(setting?.split('-')[0], 10);

	return Number.isNaN(interval) ? DEFAULT_EXPIRING_INTERVAL_DAYS : interval;
};

// null/undefined mean the company setting has not resolved yet: nothing can be called expiring.
export const isKnownInterval = (interval) => {
	return Number.isFinite(interval);
};

// rest-api serialises naive server wall-clock datetimes labelled +00:00 (the rest-api container runs in UTC), and a
// maintenance date is an instant in UTC whose written day is the day to show. Reading every value as UTC keeps the
// calendar day the server meant, whatever the browser's time zone; the day is then compared with the browser's own
// calendar day. Strict ISO parse: every real input is ISO ('2023-12-02', ISO datetimes), and a non-ISO string must not
// reach moment's deprecated Date fallback, which prints a warning (round 3 react N9).
export const toServerCalendarDate = (value) => {
	const parsed = value ? moment.utc(value, moment.ISO_8601, true) : null;

	return parsed && parsed.isValid() ? parsed.format(CALENDAR_DATE_FORMAT) : null;
};

export const daysUntil = (calendarDate) => {
	return moment(calendarDate, CALENDAR_DATE_FORMAT).diff(moment().startOf('day'), 'days');
};

// 0 days left is the last valid day (rest-api keeps an add-on active through its whole end day), so it
// is still Expiring, not Expired.
export const getValidityStatus = (daysLeft, interval) => {
	if (daysLeft < 0) {
		return VALIDITY_STATUS.EXPIRED;
	}

	if (daysLeft <= interval) {
		return VALIDITY_STATUS.EXPIRING;
	}

	return VALIDITY_STATUS.ACTIVE;
};

// Without the company interval nothing can be called Expiring, but a date in the past is Expired whatever the
// interval is. So the list and the pane agree while the setting loads: Expiring only ever appears once it is known.
const getStatusWithoutInterval = (daysLeft) => {
	return daysLeft < 0 ? VALIDITY_STATUS.EXPIRED : null;
};

// The date is known as soon as the machine is; the status needs the company interval, which loads separately.
export const getMaintenanceExpiry = (expiresOn, interval) => {
	const calendarDate = toServerCalendarDate(expiresOn);

	if (!calendarDate) {
		return null;
	}

	const daysLeft = daysUntil(calendarDate);
	const status = isKnownInterval(interval) ? getValidityStatus(daysLeft, interval) : getStatusWithoutInterval(daysLeft);

	return { calendarDate, daysLeft, status };
};

export const formatCalendarDate = (calendarDate) => {
	return moment(calendarDate, CALENDAR_DATE_FORMAT).format(DISPLAY_DATE_FORMAT);
};
